import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

type RichTextEditorProps = {
  label: string;
  value: string;
  onChange: (html: string, plainText: string) => void;
  maxChars: number;
  required?: boolean;
  error?: boolean;
  helperText?: string;
};

const DEFAULT_TOOLBAR_BG = "#F8F8F8";
const DEFAULT_BORDER_COLOR = "#E0E0E0";

const toPlainText = (html: string): string => {
  if (!html) return "";
  if (typeof window === "undefined") {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const div = window.document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

const sanitiseToHtml = (value: string): string => {
  if (!value) return "";
  if (/<[a-z][\s\S]*>/i.test(value)) {
    return value;
  }
  if (typeof window === "undefined") {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
  const div = window.document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
};

export const RichTextEditor = ({
  label,
  value,
  onChange,
  maxChars,
  required,
  error,
  helperText,
}: RichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastHtmlRef = useRef<string>("");
  const [charCount, setCharCount] = useState<number>(0);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);

  const updateFormattingStates = useCallback(() => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection?.anchorNode) return;
    if (!editorRef.current.contains(selection.anchorNode)) return;

    setIsBold(document.queryCommandState("bold"));
    setIsItalic(document.queryCommandState("italic"));
  }, []);

  const syncFromValue = useCallback(
    (incoming: string) => {
      const editor = editorRef.current;
      if (!editor) return;
      // Only sanitize if the content doesn't already contain HTML tags
      const sanitised = incoming.includes('<') ? incoming : sanitiseToHtml(incoming);
      if (sanitised === lastHtmlRef.current) return;

      editor.innerHTML = sanitised;
      lastHtmlRef.current = sanitised;
      const plain = toPlainText(sanitised);
      setCharCount(plain.length);
    },
    []
  );

  useEffect(() => {
    syncFromValue(value);
  }, [value, syncFromValue]);

  useEffect(() => {
    const handleSelectionChange = () => updateFormattingStates();
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [updateFormattingStates]);

  const emitChange = useCallback(
    (html: string) => {
      const plain = toPlainText(html);
      setCharCount(plain.length);
      lastHtmlRef.current = html;
      onChange(html, plain);
    },
    [onChange]
  );

  const enforceMaxLength = useCallback(
    (plainText: string) => {
      if (plainText.length <= maxChars) return true;
      const editor = editorRef.current;
      if (!editor) return false;

      const allowed = plainText.slice(0, maxChars);
      if (typeof window !== "undefined") {
        const div = window.document.createElement("div");
        div.textContent = allowed;
        editor.innerHTML = div.innerHTML;
        emitChange(editor.innerHTML);
        const range = window.document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      return false;
    },
    [emitChange, maxChars]
  );

  const handleInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const plain = toPlainText(editor.innerHTML);
    const isValid = enforceMaxLength(plain);
    if (!isValid) return;
    // Clean up the HTML to remove unwanted entities
    const cleanedHtml = editor.innerHTML
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
    emitChange(cleanedHtml);
  }, [emitChange, enforceMaxLength]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const editor = editorRef.current;
      if (!editor) return;
      const plain = toPlainText(editor.innerHTML);

      const isCharacter =
        event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

      if (isCharacter && plain.length >= maxChars) {
        event.preventDefault();
      }
    },
    [maxChars]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      const editor = editorRef.current;
      if (!editor) return;

      const pasteText = event.clipboardData.getData("text/plain");
      const currentPlain = toPlainText(editor.innerHTML);
      const remaining = Math.max(maxChars - currentPlain.length, 0);

      if (remaining <= 0) {
        return;
      }

      const textToInsert = pasteText.slice(0, remaining);

      if (typeof window !== "undefined") {
        window.document.execCommand("insertText", false, textToInsert);
        handleInput();
      }
    },
    [handleInput, maxChars]
  );

  const applyFormat = useCallback(
    (command: "bold" | "italic" | "insertUnorderedList") => {
      if (typeof window === "undefined") return;
      window.document.execCommand(command, false);
      updateFormattingStates();
      editorRef.current?.focus();
      handleInput();
    },
    [handleInput, updateFormattingStates]
  );

  const counterColor = useMemo(() => {
    if (error) return "error.main";
    if (charCount >= maxChars) return "error.main";
    if (charCount >= maxChars * 0.9) return "warning.main";
    return "text.secondary";
  }, [charCount, error, maxChars]);

  return (
    <Stack spacing={1.5}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="subtitle1" fontWeight={600}>
          {label}
        </Typography>
        {required && (
          <Typography variant="subtitle1" color="error.main">
            *
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          border: `1px solid ${error ? "#d32f2f" : DEFAULT_BORDER_COLOR}`,
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            backgroundColor: DEFAULT_TOOLBAR_BG,
            borderBottom: `1px solid ${DEFAULT_BORDER_COLOR}`,
            px: 1.5,
            py: 1,
          }}
        >
          <Tooltip title="Bold">
            <span>
              <IconButton
                size="small"
                onClick={() => applyFormat("bold")}
                color={isBold ? "primary" : "default"}
              >
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Italic">
            <span>
              <IconButton
                size="small"
                onClick={() => applyFormat("italic")}
                color={isItalic ? "primary" : "default"}
              >
                <FormatItalicIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Bullet list">
            <span>
              <IconButton
                size="small"
                onClick={() => applyFormat("insertUnorderedList")}
              >
                <FormatListBulletedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          sx={{
            minHeight: 160,
            px: 2.5,
            py: 2,
            fontSize: 16,
            lineHeight: 1.6,
            color: "#1E1E1E",
            "& ul": {
              pl: 3,
              mt: 0,
              mb: 0,
            },
            "& li": {
              mb: 0.5,
            },
            outline: "none",
            cursor: "text",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: DEFAULT_TOOLBAR_BG,
            borderTop: `1px solid ${DEFAULT_BORDER_COLOR}`,
            px: 1.5,
            py: 0.75,
          }}
        >
          <Typography
            variant="caption"
            color={error ? "error.main" : "text.secondary"}
          >
            {helperText || " "}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: counterColor,
              minWidth: 70,
              textAlign: "right",
              fontWeight: 500,
            }}
          >
            {charCount}/{maxChars}
          </Typography>
        </Box>
      </Box>
    </Stack>
  );
};

export default RichTextEditor;
